import React from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  ConfirmDialog,
  DefinitionList,
  ErrorState,
  FilterChip,
  Kbd,
  Meter,
  PersonCell,
  SearchField,
  Sheet,
  SpecGrid,
  StatusPill,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
  Toolbar,
} from '../../components/ui';
import type { SpecItem, StatusTone } from '../../components/ui';
import { TimeOffEntry, TimeOffStatus, TimeOffType } from '../../store/slices/timeOffSlice';
import WfhApprovalRoute from '../../components/time-off/WfhApprovalRoute';
import { wfhListFlag } from '../../components/time-off/wfhRoute';
import { useNow } from '../../hooks/useNow';
import type { Employee } from '../../store/slices/employeeSlice';
import { focusRing, sbg } from '../../theme/sbgTokens';
import { formatCount, formatDateRange, formatDateTime, formatDays, formatRelative, MISSING } from '../../utils/format';
import {
  MissingValue,
  NO_DEPARTMENT_LABEL,
  TypeTag,
  entryDays,
  formatHours,
  isTypingTarget,
  possessive,
  revealRowActions,
  startsLabel,
  typeLabel,
  useSlashToFocus,
} from './shared';

export type Decision = TimeOffStatus.APPROVED | TimeOffStatus.REJECTED;

interface ApprovalsQueueProps {
  /** Pending PTO and WFH-override requests (HRTimeOffManager's approvalEntries). */
  entries: TimeOffEntry[];
  /** Used for balances only. */
  employees: Employee[];
  today: Date;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  /** Writes the decision; resolves true when it was saved. */
  onDecide: (entry: TimeOffEntry, decision: Decision) => Promise<boolean>;
  deciding: boolean;
}

type SortKey = 'employee' | 'dates' | 'hours' | 'requested';
type SortDir = 'asc' | 'desc';

const NO_DEPARTMENT = '__none__';
const QUEUE_TYPES = [TimeOffType.PTO, TimeOffType.WFH];
const COLUMN_COUNT = 8;

interface Flag {
  key: string;
  tone: StatusTone;
  label: string;
  /** Plain-language explanation shown in the review sheet. */
  detail?: string;
  /** A tooltip for the queue row's pill, when the sheet explains it another way. */
  hint?: string;
}

const CHECK_CATEGORY = 'Check the category and reason before you decide.';

/** Review reasons set when automatic categorization of a synced request didn't work. */
const NEEDS_CATEGORY_DETAIL: Record<string, string> = {
  uncategorized: `Couldn’t categorize this request automatically. ${CHECK_CATEGORY}`,
  openai_error: `Automatic categorization failed when this request synced. ${CHECK_CATEGORY}`,
  reanalyze_failed: `Categorizing this request again didn’t work. ${CHECK_CATEGORY}`,
};

/** Why a request needs a closer look. The source ("From calendar") is not a flag; it lives in the Type cell and the sheet. */
const flagsFor = (entry: TimeOffEntry, now: Date): Flag[] => {
  const reason = entry.reviewReason || '';
  if (reason === 'pto_lead_time') {
    return [{ key: 'short-notice', tone: 'warning', label: 'Short notice', detail: 'Submitted with less than 2 weeks’ notice.' }];
  }
  // Where a WFH policy hold sits: on the employee, with the VPs, or overdue.
  // The review sheet explains it in full (WfhApprovalRoute), so no detail here.
  const wfh = wfhListFlag(entry, now);
  if (wfh) return [{ key: 'wfh-route', tone: wfh.tone, label: wfh.label, hint: wfh.detail }];
  if (NEEDS_CATEGORY_DETAIL[reason]) {
    return [{ key: 'needs-category', tone: 'info', label: 'Needs category', detail: NEEDS_CATEGORY_DETAIL[reason] }];
  }
  if (reason === 'recurring_calendar_series') {
    return [
      {
        key: 'recurring',
        tone: 'info',
        label: 'Recurring event',
        detail: 'Synced from a recurring Outlook series. Check that the dates cover only the time off intended.',
      },
    ];
  }
  if (entry.needsReview) {
    // Last resort for a reason this page doesn't know yet.
    return [
      {
        key: 'review',
        tone: 'info',
        label: 'Flagged for review',
        detail: 'Flagged for an HR check. Review the details and balances before you decide.',
      },
    ];
  }
  return [];
};

const compareBy: Record<SortKey, (a: TimeOffEntry, b: TimeOffEntry) => number> = {
  employee: (a, b) => a.employee.name.localeCompare(b.employee.name),
  dates: (a, b) => a.startDate.localeCompare(b.startDate) || a.endDate.localeCompare(b.endDate),
  hours: (a, b) => (a.hours ?? 0) - (b.hours ?? 0),
  requested: (a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''),
};

const departmentKey = (entry: TimeOffEntry) => entry.employee.department?._id || NO_DEPARTMENT;

const ApprovalsQueue: React.FC<ApprovalsQueueProps> = ({ entries, employees, today, loading, error, onRetry, onDecide, deciding }) => {
  // Re-read each minute: a VP deadline can pass while this queue stays open.
  const now = useNow();
  const [search, setSearch] = React.useState('');
  const [types, setTypes] = React.useState<string[]>([]);
  const [department, setDepartment] = React.useState('');
  const [sort, setSort] = React.useState<{ key: SortKey; dir: SortDir }>({ key: 'dates', dir: 'asc' });
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [confirm, setConfirm] = React.useState<{ entry: TimeOffEntry; decision: Decision; fromSheet: boolean } | null>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const lastOpenRef = React.useRef<TimeOffEntry | null>(null);

  useSlashToFocus(searchRef, !openId && !confirm);

  const employeeById = React.useMemo(() => new Map(employees.map((employee) => [employee._id, employee])), [employees]);

  const typeCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    entries.forEach((entry) => {
      counts[entry.type] = (counts[entry.type] || 0) + 1;
    });
    return counts;
  }, [entries]);

  const departmentOptions = React.useMemo(() => {
    const byKey = new Map<string, { key: string; name: string; count: number }>();
    entries.forEach((entry) => {
      const key = departmentKey(entry);
      const current = byKey.get(key) ?? { key, name: entry.employee.department?.name || NO_DEPARTMENT_LABEL, count: 0 };
      current.count += 1;
      byKey.set(key, current);
    });
    return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const visible = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    const rows = entries.filter((entry) => {
      if (types.length > 0 && !types.includes(entry.type)) return false;
      if (department && departmentKey(entry) !== department) return false;
      if (!query) return true;
      return (
        entry.employee.name.toLowerCase().includes(query) ||
        (entry.employee.email || '').toLowerCase().includes(query) ||
        (entry.employee.department?.name || '').toLowerCase().includes(query)
      );
    });
    const direction = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => compareBy[sort.key](a, b) * direction);
  }, [entries, search, types, department, sort]);

  const clearFilters = () => {
    setSearch('');
    setTypes([]);
    setDepartment('');
  };

  const toggleType = (type: string) =>
    setTypes((current) => (current.includes(type) ? current.filter((value) => value !== type) : [...current, type]));

  const changeSort = (key: SortKey) =>
    setSort((current) => (current.key === key ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  // Sheet: the open request, kept on screen while the drawer animates closed.
  const openEntry = openId ? visible.find((entry) => entry._id === openId) ?? entries.find((entry) => entry._id === openId) ?? null : null;
  React.useEffect(() => {
    if (openEntry) lastOpenRef.current = openEntry;
  }, [openEntry]);
  const sheetEntry = openEntry ?? lastOpenRef.current;
  const openIndex = openEntry ? visible.findIndex((entry) => entry._id === openEntry._id) : -1;

  const step = React.useCallback(
    (offset: number) => {
      if (openIndex < 0) return;
      const next = visible[openIndex + offset];
      if (next) setOpenId(next._id);
    },
    [openIndex, visible],
  );

  const requestDecision = React.useCallback((entry: TimeOffEntry, decision: Decision, fromSheet: boolean) => {
    setConfirm({ entry, decision, fromSheet });
  }, []);

  const confirmDecision = React.useCallback(async () => {
    if (!confirm || deciding) return;
    const { entry, decision, fromSheet } = confirm;
    // Work out where the sheet goes next before the decided request leaves the list.
    const index = visible.findIndex((row) => row._id === entry._id);
    const next = index >= 0 ? visible[index + 1] ?? visible[index - 1] ?? null : null;
    const saved = await onDecide(entry, decision);
    setConfirm(null);
    if (saved && fromSheet) setOpenId(next ? next._id : null);
  }, [confirm, deciding, onDecide, visible]);

  // Keyboard: A approve, D decline, J/K next/previous while the review sheet is open.
  React.useEffect(() => {
    if (!openEntry || confirm) return undefined;
    const handler = (event: KeyboardEvent) => {
      if (isTypingTarget(event)) return;
      const key = event.key.toLowerCase();
      if (key === 'a') {
        event.preventDefault();
        requestDecision(openEntry, TimeOffStatus.APPROVED, true);
      } else if (key === 'd') {
        event.preventDefault();
        requestDecision(openEntry, TimeOffStatus.REJECTED, true);
      } else if (key === 'j') {
        event.preventDefault();
        step(1);
      } else if (key === 'k') {
        event.preventDefault();
        step(-1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [openEntry, confirm, requestDecision, step]);

  // Keyboard: Enter confirms the open decision (unless focus is on a button, which handles Enter itself).
  React.useEffect(() => {
    if (!confirm || deciding) return undefined;
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest?.('button, a, input, textarea')) return;
      event.preventDefault();
      void confirmDecision();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [confirm, deciding, confirmDecision]);

  const countLabel =
    visible.length === entries.length
      ? formatCount(entries.length, 'request')
      : `${visible.length} of ${formatCount(entries.length, 'request')}`;

  const departmentName = departmentOptions.find((option) => option.key === department)?.name;

  const toolbar = (
    <Toolbar
      end={
        !loading && !error ? (
          <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }} aria-live="polite">
            {countLabel}
          </Typography>
        ) : undefined
      }
    >
      <SearchField value={search} onChange={setSearch} label="Search requests" placeholder="Search by name or department" inputRef={searchRef} />
      <Box role="group" aria-label="Filter by type" sx={{ display: 'flex', gap: 1 }}>
        {QUEUE_TYPES.map((type) => (
          <FilterChip key={type} selected={types.includes(type)} onClick={() => toggleType(type)} count={typeCounts[type] || 0}>
            {typeLabel(type)}
          </FilterChip>
        ))}
      </Box>
      <Select
        size="small"
        value={department}
        onChange={(event) => setDepartment(event.target.value)}
        displayEmpty
        inputProps={{ 'aria-label': 'Filter by department' }}
        renderValue={(value) => (value ? departmentName ?? 'Department' : 'All departments')}
        sx={{ minWidth: 184, maxWidth: { xs: '100%', sm: 260 } }}
      >
        <MenuItem value="">All departments</MenuItem>
        {departmentOptions.map((option) => (
          <MenuItem key={option.key} value={option.key} sx={{ gap: 2 }}>
            <Box component="span" sx={{ flex: 1 }}>{option.name}</Box>
            <Box component="span" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>{option.count}</Box>
          </MenuItem>
        ))}
      </Select>
    </Toolbar>
  );

  const sortHeader = (key: SortKey, label: string, align: 'left' | 'right' = 'left') => (
    <TableCell align={align} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : 'asc'} onClick={() => changeSort(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const keyboardHints = (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 2,
        rowGap: 0.5,
        px: 2,
        py: 1.25,
        borderTop: `1px solid ${sbg.border.subtle}`,
        color: sbg.text.secondary,
      }}
    >
      {[
        { keys: ['/'], label: 'Search' },
        { keys: ['A'], label: 'Approve' },
        { keys: ['D'], label: 'Decline' },
        { keys: ['J', 'K'], label: 'Next and previous' },
      ].map((hint) => (
        <Box key={hint.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          {hint.keys.map((key) => (
            <Kbd key={key}>{key}</Kbd>
          ))}
          <Typography variant="caption" component="span">
            {hint.label}
          </Typography>
        </Box>
      ))}
      <Typography variant="caption" component="span" sx={{ ml: 'auto' }}>
        Decision shortcuts work while a request is open.
      </Typography>
    </Box>
  );

  let body: React.ReactNode;
  if (error) {
    body = (
      <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
        <TableCell colSpan={COLUMN_COUNT} sx={{ borderBottom: 0 }}>
          <ErrorState
            compact
            title="Couldn’t load time-off requests"
            body="The server didn’t respond. Try again in a minute; your filters are kept."
            onRetry={onRetry}
          />
        </TableCell>
      </TableRow>
    );
  } else if (loading) {
    body = <TableSkeletonRows columns={COLUMN_COUNT} />;
  } else if (entries.length === 0) {
    body = (
      <TableEmptyRow
        colSpan={COLUMN_COUNT}
        title="No requests waiting"
        body="PTO requests and WFH overrides that need an HR decision appear here."
      />
    );
  } else if (visible.length === 0) {
    const parts = [
      search.trim() ? `“${search.trim()}”` : null,
      types.length > 0 ? types.map(typeLabel).join(' or ') : null,
      departmentName ?? null,
    ].filter(Boolean);
    body = (
      <TableEmptyRow
        colSpan={COLUMN_COUNT}
        title="No requests match these filters"
        body={parts.length > 0 ? `Nothing in the queue matches ${parts.join(' · ')}.` : undefined}
        action={
          <Button variant="outlined" size="small" onClick={clearFilters}>
            Clear filters
          </Button>
        }
      />
    );
  } else {
    body = visible.map((entry) => {
      const employee = employeeById.get(entry.employee._id);
      const flags = flagsFor(entry, now);
      const days = entryDays(entry.startDate, entry.endDate);
      const startHint = startsLabel(entry.startDate, today);
      const ptoLeft = employee && entry.type === TimeOffType.PTO && employee.ptoAllotment > 0 ? employee.ptoAllotment - employee.ptoUsed : null;
      const name = entry.employee.name;
      return (
        <TableRow
          key={entry._id}
          hover
          selected={openId === entry._id}
          onClick={() => setOpenId(entry._id)}
          sx={{ cursor: 'pointer', ...revealRowActions }}
        >
          <TableCell sx={{ minWidth: 220 }}>
            <PersonCell
              avatarName={name}
              name={
                <ButtonBase
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpenId(entry._id);
                  }}
                  aria-label={`Review ${possessive(name)} request`}
                  sx={{
                    font: 'inherit',
                    color: 'inherit',
                    borderRadius: sbg.radius.xs,
                    maxWidth: '100%',
                    justifyContent: 'flex-start',
                    '&:hover': { textDecoration: 'underline', textUnderlineOffset: '2px' },
                    '&.Mui-focusVisible': focusRing,
                  }}
                >
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}
                  </Box>
                </ButtonBase>
              }
              secondary={entry.employee.department?.name || NO_DEPARTMENT_LABEL}
            />
          </TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            <TypeTag type={entry.type} />
            {entry.isFromCalendar && (
              <Typography variant="body2" component="div" sx={{ color: sbg.text.secondary, mt: 0.5 }}>
                From calendar
              </Typography>
            )}
          </TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            <Typography variant="body1" component="div" sx={{ color: sbg.text.primary }}>
              {formatDateRange(entry.startDate, entry.endDate)}
            </Typography>
            {startHint && (
              <Typography variant="body2" component="div" sx={{ color: sbg.text.secondary }}>
                {startHint}
              </Typography>
            )}
          </TableCell>
          <TableCell>
            {flags.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {flags.map((flag) => (flag.hint ? (
                  <Tooltip key={flag.key} title={flag.hint}>
                    <Box component="span" sx={{ display: 'inline-flex' }}>
                      <StatusPill tone={flag.tone}>{flag.label}</StatusPill>
                    </Box>
                  </Tooltip>
                ) : (
                  <StatusPill key={flag.key} tone={flag.tone}>
                    {flag.label}
                  </StatusPill>
                )))}
              </Box>
            ) : (
              <MissingValue />
            )}
          </TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            <Typography variant="body1" component="div" sx={{ color: sbg.text.primary }}>
              {formatHours(entry.hours)}
            </Typography>
            {days > 1 && (
              <Typography variant="body2" component="div" sx={{ color: sbg.text.secondary }}>
                {formatDays(days, 0)}
              </Typography>
            )}
          </TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            {ptoLeft === null ? (
              <MissingValue />
            ) : (
              <Box component="span" sx={{ color: ptoLeft < 0 ? sbg.status.danger.text : sbg.text.primary }}>
                {formatHours(ptoLeft)}
              </Box>
            )}
          </TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap', color: sbg.text.secondary }}>
            {entry.createdAt ? (
              <Tooltip title={formatDateTime(entry.createdAt)}>
                <span>{formatRelative(entry.createdAt)}</span>
              </Tooltip>
            ) : (
              MISSING
            )}
          </TableCell>
          <TableCell align="right" onClick={(event) => event.stopPropagation()} sx={{ whiteSpace: 'nowrap', py: 0 }}>
            <Box className="row-actions" sx={{ display: 'inline-flex', gap: 0.5 }}>
              <Tooltip title={`Decline ${possessive(name)} request`}>
                <IconButton size="small" aria-label={`Decline ${possessive(name)} request`} onClick={() => requestDecision(entry, TimeOffStatus.REJECTED, false)}>
                  <X />
                </IconButton>
              </Tooltip>
              <Tooltip title={`Approve ${possessive(name)} request`}>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={`Approve ${possessive(name)} request`}
                  onClick={() => requestDecision(entry, TimeOffStatus.APPROVED, false)}
                >
                  <Check />
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>
        </TableRow>
      );
    });
  }

  const confirmApprove = confirm?.decision === TimeOffStatus.APPROVED;

  return (
    <>
      <TableCard aria-label="Requests waiting for a decision" toolbar={toolbar} footer={!loading && !error && entries.length > 0 ? keyboardHints : undefined}>
        <Table aria-label="Time-off requests waiting for a decision">
          <TableHead>
            <TableRow>
              {sortHeader('employee', 'Employee')}
              <TableCell>Type</TableCell>
              {sortHeader('dates', 'Dates')}
              <TableCell>Flags</TableCell>
              {sortHeader('hours', 'Hours', 'right')}
              <TableCell align="right">PTO left</TableCell>
              {sortHeader('requested', 'Requested')}
              <TableCell align="right">
                <span className="sbg-sr-only">Actions</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{body}</TableBody>
        </Table>
      </TableCard>

      <RequestSheet
        entry={sheetEntry}
        employee={sheetEntry ? employeeById.get(sheetEntry.employee._id) : undefined}
        open={Boolean(openEntry)}
        today={today}
        position={openIndex >= 0 ? openIndex + 1 : null}
        total={visible.length}
        onClose={() => setOpenId(null)}
        onPrevious={openIndex > 0 ? () => step(-1) : undefined}
        onNext={openIndex >= 0 && openIndex < visible.length - 1 ? () => step(1) : undefined}
        onDecide={(entry, decision) => requestDecision(entry, decision, true)}
      />

      <ConfirmDialog
        open={Boolean(confirm)}
        onClose={() => {
          if (!deciding) setConfirm(null);
        }}
        onConfirm={() => {
          void confirmDecision();
        }}
        title={
          confirm
            ? `${confirmApprove ? 'Approve' : 'Decline'} ${possessive(confirm.entry.employee.name)} ${typeLabel(confirm.entry.type)}?`
            : ''
        }
        body={
          confirm
            ? `${formatDateRange(confirm.entry.startDate, confirm.entry.endDate)} · ${formatHours(confirm.entry.hours)}. The request leaves this queue as ${
                confirmApprove ? 'approved' : 'declined'
              }.`
            : undefined
        }
        confirmLabel={confirmApprove ? 'Approve request' : 'Decline request'}
        loadingLabel={confirmApprove ? 'Approving…' : 'Declining…'}
        destructive={!confirmApprove}
        loading={deciding}
      />
    </>
  );
};

export default ApprovalsQueue;

/* ------------------------------------------------------------------------ */
/* Review sheet: the whole request, balances and the decision in one place   */
/* ------------------------------------------------------------------------ */

interface RequestSheetProps {
  entry: TimeOffEntry | null;
  employee?: Employee;
  open: boolean;
  today: Date;
  position: number | null;
  total: number;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onDecide: (entry: TimeOffEntry, decision: Decision) => void;
}

const BalanceMeter: React.FC<{ label: string; used: number; allotment: number }> = ({ label, used, allotment }) => {
  if (!allotment) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
          {label}
        </Typography>
        <Typography variant="labelSm" component="span" sx={{ color: sbg.text.secondary }}>
          No allotment
        </Typography>
      </Box>
    );
  }
  const left = allotment - used;
  const state = left < 0 ? `${formatHours(Math.abs(left))} over` : `${formatHours(left)} left`;
  return (
    <Meter
      // The bar stops at the allotment; the label carries the overage.
      value={Math.min(used, allotment)}
      max={allotment}
      tone={used > allotment ? 'danger' : 'brand'}
      label={label}
      valueLabel={`${formatHours(used)} used of ${formatHours(allotment)} · ${state}`}
      aria-label={`${label}: ${formatHours(used)} used of ${formatHours(allotment)}, ${state}`}
    />
  );
};

const RequestSheet: React.FC<RequestSheetProps> = ({ entry, employee, open, today, position, total, onClose, onPrevious, onNext, onDecide }) => {
  const now = useNow();
  if (!entry) return null;
  const flags = flagsFor(entry, now);
  const days = entryDays(entry.startDate, entry.endDate);
  const startHint = startsLabel(entry.startDate, today);

  const spec: SpecItem[] = [
    { label: 'Dates', value: formatDateRange(entry.startDate, entry.endDate), caption: startHint ?? undefined },
    { label: 'Hours', value: formatHours(entry.hours), caption: formatDays(days, 0) },
  ];
  if (entry.type === TimeOffType.PTO && employee && employee.ptoAllotment > 0) {
    spec.push({
      label: 'PTO left',
      value: formatHours(employee.ptoAllotment - employee.ptoUsed),
      caption: `of ${formatHours(employee.ptoAllotment)} allotted`,
    });
  }

  const details: { label: React.ReactNode; value: React.ReactNode }[] = [
    { label: 'Manager', value: entry.employee.managerName || MISSING },
    { label: 'Source', value: entry.isFromCalendar ? 'Outlook calendar' : 'Logged in Attendance' },
    { label: 'Requested', value: entry.createdAt ? formatDateTime(entry.createdAt) : MISSING },
  ];
  if (entry.category) details.push({ label: 'Category', value: entry.category });
  if (entry.reason) details.push({ label: 'Reason', value: entry.reason });
  if (entry.originalCalendarSubject) details.push({ label: 'Calendar event', value: entry.originalCalendarSubject });
  if (entry.originalCalendarLocation) details.push({ label: 'Location', value: entry.originalCalendarLocation });
  if (entry.notes) details.push({ label: 'Notes', value: entry.notes });

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={entry.employee.name}
      subtitle={[`${typeLabel(entry.type)} request`, entry.employee.department?.name, entry.employee.email].filter(Boolean).join(' · ')}
      actions={
        <>
          <Button component={Link} to={`/employees/${entry.employee._id}`} variant="text" onClick={onClose} sx={{ mr: 'auto' }}>
            View profile
          </Button>
          <Button variant="outlined" startIcon={<X />} onClick={() => onDecide(entry, TimeOffStatus.REJECTED)}>
            Decline request
          </Button>
          <Button variant="contained" startIcon={<Check />} onClick={() => onDecide(entry, TimeOffStatus.APPROVED)}>
            Approve request
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          <StatusPill tone="warning">Pending approval</StatusPill>
          {flags.map((flag) => (
            <StatusPill key={flag.key} tone={flag.tone}>
              {flag.label}
            </StatusPill>
          ))}
        </Box>
        {position !== null && total > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums', mr: 0.5 }}>
              {position} of {total}
            </Typography>
            <Tooltip title="Previous request">
              <span>
                <IconButton size="small" aria-label="Previous request" onClick={onPrevious} disabled={!onPrevious}>
                  <ChevronLeft />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next request">
              <span>
                <IconButton size="small" aria-label="Next request" onClick={onNext} disabled={!onNext}>
                  <ChevronRight />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>

      <SpecGrid items={spec} minColumnWidth={180} />

      <WfhApprovalRoute entry={entry} />

      {flags
        .filter((flag) => flag.detail)
        .map((flag) => (
          <Alert key={flag.key} severity={flag.tone === 'warning' ? 'warning' : 'info'}>
            {flag.detail}
          </Alert>
        ))}

      <Box component="section" aria-labelledby="request-details-title" sx={{ display: 'grid', gap: 0.5 }}>
        <Typography id="request-details-title" variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
          Details
        </Typography>
        <DefinitionList items={details} />
      </Box>

      <Box component="section" aria-labelledby="request-balances-title" sx={{ display: 'grid', gap: 1.5 }}>
        <Typography id="request-balances-title" variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
          Balances
        </Typography>
        {employee ? (
          <>
            <BalanceMeter label="PTO" used={employee.ptoUsed} allotment={employee.ptoAllotment} />
            <BalanceMeter label="Sick" used={employee.sickTimeUsed} allotment={employee.sickTimeAllotment} />
          </>
        ) : (
          <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
            Balances aren’t available for this employee. Their profile may be inactive.
          </Typography>
        )}
      </Box>
    </Sheet>
  );
};
